import { withApollo } from "@apollo/client/react/hoc";
import { Loader } from "@screencloud/screencloud-ui-components";
import { find, isUndefined } from "lodash";
import { useEffect } from "react";
import { FormattedMessage, injectIntl } from "react-intl";
import { SortingActions, UUID } from "../../constants/constants";
import { PlaylistListItemFragment } from "../../types.g";
import { compose } from "../../utils/compose";
import EmptyState from "../EmptyState";
import {
  useSearchPlaylistPicker,
  UseSearchPlaylistPicker,
} from "./hooks/useSearchPlaylistPicker";
import { Item } from "./Item";
import { useAppContext } from "src/hooks/useAppContext";
import { useTagSelection, UseTagSelection } from "../TagList/useTagSelection";
export interface ApolloProps extends UseSearchPlaylistPicker, UseTagSelection {}

interface SearchPlaylistPickerOwnProps {
  multiple?: boolean;
  searchString: string;
  allowSelectAll?: boolean;
  selectedArray: string[];
  selectedData: PlaylistListItemFragment[];
  isSelectAll: boolean;
  onSelectAllChange: () => void;
  toggleSelect: (
    playlistIds: string,
    playlist: Partial<PlaylistListItemFragment>,
  ) => void;
  onSearchSelectAll: (
    playlistIds: string[],
    playlist: Partial<PlaylistListItemFragment>[],
  ) => void;
  spaceId?: UUID;
}

const withData = compose(
  withApollo,
  injectIntl,
  (Component) => (props: SearchPlaylistPickerOwnProps) => {
    const { selectedTagForQuery } = useTagSelection();
    const searchPlaylist = useSearchPlaylistPicker(
      props.searchString,
      props.spaceId as UUID,
      selectedTagForQuery,
    );

    return <Component {...props} {...searchPlaylist} {...useTagSelection()} />;
  },
);

export interface PlaylistPickerState {
  playlistCount: number;
  searchString: string;
  sortBy: SortingActions;
  isFetchMore: boolean;
}

export const SearchPlaylistPickerComponent = (
  props: SearchPlaylistPickerOwnProps & ApolloProps,
): JSX.Element => {
  const {
    allowSelectAll,
    playlists,
    loading,
    renderFetchMoreButton,
    toggleSelect,
    selectedArray,
    isSelectAll,
    onClickTag,
  } = props;

  const context = useAppContext();

  useEffect(() => {
    if (isSelectAll) {
      const ids = (playlists ?? []).map((playlist) => playlist.id);
      props.onSearchSelectAll(ids, playlists);
    }
  }, [isSelectAll]);

  const isItemSelected = (id: UUID): boolean => {
    const result = find(selectedArray, (selectedItemId) => {
      return selectedItemId === id;
    });
    return !isUndefined(result);
  };

  if (loading) {
    return <Loader active />;
  }

  return (
    <>
      {playlists.length > 0 ? (
        <>
          {playlists.map((playlist, index) => {
            const draftPlaylist = playlist?.draft;
            const isPlaylistOwner =
              playlist.spaceId === context?.currentSpace?.id;
            return (
              draftPlaylist !== null && (
                <Item
                  isSelected={isItemSelected(playlist?.id)}
                  playlist={playlist}
                  allowSelectAll={allowSelectAll}
                  isOwner={isPlaylistOwner}
                  index={index}
                  callback={toggleSelect}
                  onClickTag={onClickTag}
                />
              )
            );
          })}
          {renderFetchMoreButton}
        </>
      ) : (
        <EmptyState section="search-playlists" className="empty">
          <h3>
            <FormattedMessage
              id="common.search.no_results"
              defaultMessage="We couldn’t find any matches"
            />
          </h3>
          <p>
            <FormattedMessage
              id="common.search.no_results_description"
              defaultMessage="Try adjusting or using different search terms."
            />
          </p>
        </EmptyState>
      )}
    </>
  );
};

export default withData(SearchPlaylistPickerComponent);
